// ModifierBuilderModal.tsx - Dynamic version based on MenuItem modifiers
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
  useMemo
} from "react";
import {
  LazyMotion,
  domAnimation,
  m,
  useInView,
  AnimatePresence
} from "framer-motion";
import {
  MdClose,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdRadioButtonChecked,
  MdRadioButtonUnchecked,
  MdShoppingCart,
  MdCheckCircle,
  MdExpandMore,
  MdExpandLess,
  MdRestaurant
} from "react-icons/md";
import { MenuItem } from "../redux/slices/menuSlice";
import { filterEnabledModifiers } from "../utils/modifierUtils";
import {
  isBuilderModifierRequired,
  isBuilderModifierMultiSelect,
  isBuilderSectionComplete,
  isBuilderSectionEnabled,
  canBuilderAddToCart,
  handleBuilderOptionSelection,
  isBuilderOptionSelected,
  getBuilderSelectionCount,
  getBuilderMaxSelections,
  isBuilderMaxReached,
  type BuilderModifier,
  type BuilderSelection,
} from "../utils/builderModifierUtils";
import { formatPrice, parsePrice } from "../utils/priceUtils";

/* ------------------------------- Types ----------------------------------- */

interface ModifierOption {
  id: string | number;
  name: string;
  price: number | string;
}

interface SelectedModifier {
  name: string;
  options: ModifierOption[];
}

interface ModifierBuilderModalProps {
  isOpen: boolean;
  item: MenuItem | null;
  onClose: () => void;
  onAddToCart: (
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: SelectedModifier[]
  ) => Promise<void> | void;
}

/* -------------------------- Motion Variants ------------------------------- */

const sectionVariants = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] as any }
  }
};

const optionVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.03, duration: 0.28, ease: "easeOut" as any }
  })
};

/* ------------------------ Helper Hooks & Utils --------------------------- */

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(m.matches);
    handler();
    m.addEventListener?.("change", handler);
    return () => m.removeEventListener?.("change", handler);
  }, []);
  return reduced;
};

/* ------------------------ ModifierSection (memo) ------------------------- */

interface Modifier {
  name: string;
  options: ModifierOption[];
  max_options_to_select?: number;
  required?: boolean;
  type?: string;
}

type ModifierSectionProps = {
  modifier: Modifier;
  selectedModifiers: SelectedModifier[];
  onSelect: (modifierName: string, option: ModifierOption) => void;
  isSelected: (modifierName: string, optionName: string) => boolean;
  activeSection: string;
  reducedMotion: boolean;
  isEnabled: boolean;
  isComplete: boolean;
};

const ModifierSection: React.FC<ModifierSectionProps> = memo(
  ({ modifier, selectedModifiers, onSelect, isSelected, activeSection, reducedMotion, isEnabled, isComplete }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const inView = useInView(ref, { margin: "-120px 0px -120px 0px", amount: 0.15 });
    const [isExpanded, setIsExpanded] = useState(false);

    // Update expanded state when section becomes enabled
    useEffect(() => {
      if (isEnabled) {
        setIsExpanded(true);
      }
    }, [isEnabled]);

    const shouldShow = inView || activeSection === modifier.name;

    const selCount = selectedModifiers.find(m => m.name === modifier.name)?.options.length ?? 0;
    const maxSelectionsValue = getBuilderMaxSelections(modifier as any);
    const maxSelections = maxSelectionsValue === null ? "∞" : maxSelectionsValue;

    const toggleExpanded = () => {
      if (!isEnabled) {
        setIsExpanded(!isExpanded);
      }
    };

    return (
      <m.section
        ref={ref}
        id={modifier.name}
        initial="hidden"
        animate={reducedMotion ? {} : shouldShow ? "visible" : "hidden"}
        variants={sectionVariants}
        className={`rounded-lg shadow-md ${
          !isEnabled && !isExpanded ? "p-3" : "p-6"
        } ${
          !isEnabled ? "bg-gray-100 opacity-60" : "bg-white"
        }`}
      >
        <div className={`flex justify-between items-center ${(isEnabled || isExpanded) ? "mb-4" : ""}`}>
          <div className="flex items-center gap-2 flex-1">
            {!isEnabled && (
              <button
                onClick={toggleExpanded}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                aria-label={isExpanded ? "Collapse section" : "Expand section"}
                type="button"
              >
                {isExpanded ? (
                  <MdExpandLess className="w-5 h-5 text-gray-600" />
                ) : (
                  <MdExpandMore className="w-5 h-5 text-gray-600" />
                )}
              </button>
            )}
            <h2 className="text-base sm:text-lg font-medium text-gray-900 flex items-center flex-wrap gap-2">
              <span>{modifier.name}</span>
              {isBuilderModifierRequired(modifier as any) && (
                <span className="text-red-500 text-xl font-bold">*</span>
              )}
              <span className="text-xs sm:text-sm text-gray-600 font-normal">
                {isBuilderModifierRequired(modifier as any) 
                  ? `(Choose ${maxSelections})`
                  : "(Optional)"
                }
              </span>
              {isComplete && isEnabled && <MdCheckCircle className="text-green-500" />}
            </h2>
          </div>
          <span className="text-xs sm:text-sm font-medium text-gray-600 bg-gray-200 px-2 sm:px-3 py-1 rounded-full whitespace-nowrap">
            {selCount}/{maxSelections}
          </span>
        </div>

        {(isEnabled || isExpanded) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {modifier.options.map((option: ModifierOption, i: number) => {
              const selected = isSelected(modifier.name, option.name);
              const optionPrice = parsePrice(option.price);
              const isPremium = optionPrice > 0;
              const isMultiSelect = isBuilderModifierMultiSelect(modifier as any);
              
              // Check if this option is disabled due to max selection reached OR section is locked
              const maxNum = typeof maxSelections === 'number' ? maxSelections : Infinity;
              const isLimitReached = isMultiSelect && selCount >= maxNum && !selected;
              const isOptionDisabled = !isEnabled || isLimitReached;

              // Determine which icon to use based on selection state and modifier type
              const IconComponent = isMultiSelect
                ? selected ? MdCheckBox : MdCheckBoxOutlineBlank
                : selected ? MdRadioButtonChecked : MdRadioButtonUnchecked;

              return (
                <m.button
                  key={option.id}
                  onClick={() => isEnabled && onSelect(modifier.name, option)}
                  custom={i}
                  variants={optionVariants}
                  initial="hidden"
                  animate={reducedMotion ? {} : shouldShow ? "visible" : "hidden"}
                  disabled={isOptionDisabled}
                  className={`relative flex items-center space-x-2 p-3 rounded-lg border transition-all text-left ${
                    selected
                      ? "border-brand-red bg-[#FEF3F3] text-brand-red"
                      : isOptionDisabled
                      ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed opacity-50"
                      : "border-gray-300 hover:bg-gray-100"
                  }`}
                  aria-pressed={selected}
                  aria-disabled={isOptionDisabled}
                  type="button"
                >
                  <IconComponent 
                    className="text-lg w-5 h-5 flex-shrink-0" 
                    style={{ color: selected ? '#EF4346' : '#D1D5DB' }}
                  />
                  <span className="font-medium text-sm">{option.name}</span>
                  {isPremium && (
                    <span className="absolute top-0 right-0 -mt-2 -mr-2 bg-gray-300 text-brand-red text-xs font-bold px-2 py-1 rounded-full">
                      {formatPrice(optionPrice)}
                    </span>
                  )}
                </m.button>
              );
            })}
          </div>
        )}
      </m.section>
    );
  }
);

ModifierSection.displayName = "ModifierSection";

/* ----------------------- ModifierBuilderModal ---------------------------- */

export const ModifierBuilderModal: React.FC<ModifierBuilderModalProps> = ({
  isOpen,
  item,
  onClose,
  onAddToCart
}) => {
  const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifier[]>([]);
  const [activeSection, setActiveSection] = useState<string>("");
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const lastActiveRef = useRef<HTMLElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  // Get enabled modifiers from item
  const modifiersList = useMemo(
    () => (item ? filterEnabledModifiers(item.modifiers_list || []) : []),
    [item?.modifiers_list]
  );

  // Reset when modal opens
  useEffect(() => {
    if (isOpen && item) {
      // Scroll to top when modal opens
      if (contentRef.current) {
        contentRef.current.scrollTo(0, 0);
      }
      
      setSelectedModifiers([]);
      setActiveSection(modifiersList[0]?.name || "");
      setError(null);
      setIsAddingToCart(false);
      setQuantity(1);
    }
  }, [isOpen, item, modifiersList]);

  // ESC to close & focus trap
  useEffect(() => {
    if (!isOpen) return;

    const previous = document.activeElement as HTMLElement | null;
    lastActiveRef.current = previous;

    const modalRoot = contentRef.current?.closest("[role='dialog']") as HTMLElement | null;
    setTimeout(() => {
      const focusable = modalRoot?.querySelectorAll<HTMLElement>(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
      );
      focusable?.[0]?.focus?.();
    }, 0);

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }

      if (e.key === "Tab") {
        if (!modalRoot) return;
        const focusable = Array.from(
          modalRoot.querySelectorAll<HTMLElement>(
            "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
          )
        ).filter(el => !el.hasAttribute("disabled") && el.offsetParent !== null);
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKey, { capture: true });
    return () => {
      document.removeEventListener("keydown", handleKey, { capture: true });
      lastActiveRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  // Scroll tracking for active section
  useEffect(() => {
    const content = contentRef.current;
    if (!content || modifiersList.length === 0) return;

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const headerHeight = headerRef.current?.getBoundingClientRect().height ?? 0;
        const scrollY = content.scrollTop + headerHeight + 16;

        for (let i = modifiersList.length - 1; i >= 0; i--) {
          const modifier = modifiersList[i];
          const el = sectionRefs.current[modifier.name];
          if (el && el.offsetTop <= scrollY) {
            setActiveSection(modifier.name);
            break;
          }
        }

        ticking = false;
      });
    };

    content.addEventListener("scroll", handleScroll, { passive: true });
    return () => content.removeEventListener("scroll", handleScroll);
  }, [modifiersList]);

  const handleModifierOptionSelect = useCallback(
    (modifierName: string, option: ModifierOption) => {
      const modifier = modifiersList.find(m => m.name === modifierName);
      if (!modifier) return;
      
      setSelectedModifiers((prev) =>
        handleBuilderOptionSelection(modifierName, option, prev as BuilderSelection[], modifier as BuilderModifier)
      );
    },
    [modifiersList]
  );

  const isModifierOptionSelectedLocal = useCallback(
    (modifierName: string, optionName: string): boolean =>
      isBuilderOptionSelected(modifierName, optionName, selectedModifiers as BuilderSelection[]),
    [selectedModifiers]
  );

  // Check if a section is complete
  const isSectionComplete = useCallback((modifier: any): boolean => {
    return isBuilderSectionComplete(modifier as BuilderModifier, selectedModifiers as BuilderSelection[]);
  }, [selectedModifiers]);

  // Check if a section should be enabled
  const isSectionEnabled = useCallback((index: number): boolean => {
    return isBuilderSectionEnabled(index, modifiersList as BuilderModifier[], selectedModifiers as BuilderSelection[]);
  }, [modifiersList, selectedModifiers]);

  const isFormValid = useCallback(() => {
    if (!item) return false;
    return canBuilderAddToCart(modifiersList as BuilderModifier[], selectedModifiers as BuilderSelection[]);
  }, [item, modifiersList, selectedModifiers]);

  const handleAddToCart = useCallback(async () => {
    if (!item || !isFormValid()) return;

    setIsAddingToCart(true);
    setError(null);

    try {
      await onAddToCart(item, quantity, null, selectedModifiers);
      onClose();
    } catch (err) {
      console.error("Add to cart failed:", err);
      setError("Failed to add item to cart. Please try again.");
    } finally {
      setIsAddingToCart(false);
    }
  }, [item, quantity, selectedModifiers, onAddToCart, onClose, isFormValid]);

  const scrollToSection = useCallback((modifierName: string) => {
    const content = contentRef.current;
    const sectionEl = sectionRefs.current[modifierName];
    if (!content || !sectionEl) return;

    // Get precise distance between section and scroll container
    const contentRect = content.getBoundingClientRect();
    const sectionRect = sectionEl.getBoundingClientRect();

    // Calculate how far to scroll with consistent top margin (32px spacing)
    const scrollTop = sectionRect.top - contentRect.top + content.scrollTop - 32;

    // Smooth scroll to the target
    content.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });

    setActiveSection(modifierName);
  }, []);

  // Auto-scroll to next incomplete section when selections change
  useEffect(() => {
    if (!contentRef.current || modifiersList.length === 0 || !isOpen) return;
    
    // Find the first incomplete enabled section
    for (let i = 0; i < modifiersList.length; i++) {
      const modifier = modifiersList[i];
      if (isSectionEnabled(i) && !isSectionComplete(modifier)) {
        // Delay to allow section to expand before scrolling
        setTimeout(() => {
          scrollToSection(modifier.name);
        }, 150);
        break;
      }
    }
  }, [selectedModifiers, modifiersList, isOpen, isSectionEnabled, isSectionComplete, scrollToSection]);

  if (!isOpen || !item) return null;

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modifier-modal-title"
        >
          <div
            className="bg-gray-50 rounded-2xl shadow-xl w-[80%] max-h-[90vh] flex flex-col relative"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div ref={headerRef} className="bg-white border-b border-brand-red p-4 sm:p-6 flex justify-between items-center">
              <div>
                <h1 id="modifier-modal-title" className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <MdRestaurant className="text-brand-red" style={{ color: '#EF4346' }} />
                  {item.name}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">{item.description || "Customize Your Selection"}</p>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  {formatPrice(parsePrice(item.price))}
                </p>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close modal"
                >
                  <MdClose className="w-6 h-6 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Two-Column Layout: Left Sidebar + Right Content */}
            <div className="flex flex-1 overflow-hidden">
              {/* Left Sidebar - Vertical Navigation */}
              {modifiersList.length > 0 && (
                <nav className="w-64 bg-white border-r border-brand-red overflow-y-auto">
                  <div className="p-4">
                    <ul className="space-y-2">
                      {modifiersList.map((modifier, idx) => {
                        const isComplete = isSectionComplete(modifier);
                        const isEnabled = isSectionEnabled(idx);
                        return (
                          <li key={modifier.name}>
                            <button
                              onClick={() => scrollToSection(modifier.name)}
                              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all border-2 ${
                                activeSection === modifier.name
                                  ? "bg-[#FEF3F3]"
                                  : isEnabled
                                  ? "text-gray-700 border-transparent hover:bg-gray-100"
                                  : "text-gray-400 border-transparent bg-gray-50"
                              }`}
                              style={{
                                color: activeSection === modifier.name ? '#EF4346' : undefined,
                                borderColor: activeSection === modifier.name ? '#EF4346' : undefined
                              }}
                              aria-current={activeSection === modifier.name ? "true" : undefined}
                              type="button"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm">{modifier.name}</span>
                                {isComplete && isEnabled && (
                                  <MdCheckCircle className="text-green-500 ml-2" />
                                )}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </nav>
              )}

              {/* Right Content Area */}
              <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-8 pb-[50vh] bg-gray-50">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                    <p className="text-red-600 text-sm font-medium">{error}</p>
                  </div>
                )}

                <div className="max-w-5xl mx-auto space-y-8">
                  {modifiersList.map((modifier, idx) => (
                    <div
                      key={modifier.name}
                      ref={el => {
                        sectionRefs.current[modifier.name] = el;
                      }}
                    >
                      <ModifierSection
                        modifier={modifier}
                        selectedModifiers={selectedModifiers}
                        onSelect={handleModifierOptionSelect}
                        isSelected={isModifierOptionSelectedLocal}
                        activeSection={activeSection}
                        reducedMotion={reducedMotion}
                        isEnabled={isSectionEnabled(idx)}
                        isComplete={isSectionComplete(modifier)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <footer className="absolute bottom-0 left-0 right-0 bg-white border-t border-brand-red p-3 sm:p-4 shadow-lg z-[9999] opacity-100 visible">
              <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="text-center sm:text-left flex-1 min-w-0">
                  {selectedModifiers.length > 0 && (
                    <p className="text-xs sm:text-sm font-medium text-gray-700 break-words">
                      Includes: {selectedModifiers.flatMap(m => m.options.map(o => o.name)).join(', ')}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3" style={{ opacity: 1, visibility: 'visible', display: 'flex' }}>
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1" style={{ opacity: 1, visibility: 'visible' }}>
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="w-8 h-8 flex items-center justify-center rounded bg-white hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Decrease quantity"
                    >
                      <span className="text-xl font-bold text-gray-700">−</span>
                    </button>
                    <span className="w-8 text-center font-bold text-gray-900">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center rounded bg-white hover:bg-gray-200 transition-colors"
                      aria-label="Increase quantity"
                    >
                      <span className="text-xl font-bold text-gray-700">+</span>
                    </button>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    onClick={handleAddToCart}
                    disabled={isAddingToCart || !isFormValid()}
                    style={{
                      opacity: 1,
                      visibility: 'visible',
                      display: 'flex',
                      backgroundColor: isFormValid() && !isAddingToCart ? '#EF4345' : 'rgb(209, 213, 219)',
                      color: isFormValid() && !isAddingToCart ? 'white' : 'rgb(75, 85, 99)'
                    }}
                    className={`font-bold py-2 sm:py-3 px-6 sm:px-8 rounded-lg transition-colors text-sm sm:text-base whitespace-nowrap flex items-center gap-2 ${
                      isFormValid() && !isAddingToCart
                        ? "hover:bg-red-600"
                        : "cursor-not-allowed"
                    }`}
                  >
                    {isAddingToCart ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Adding...</span>
                      </>
                    ) : (
                      <>
                        <MdShoppingCart className="text-lg" />
                        <span>Add to Cart</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </AnimatePresence>
    </LazyMotion>
  );
};

export default ModifierBuilderModal;
